// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Tile,
  ClickableTile,
  ComboBox,
  Form,
  Stack,
  Button,
  Grid,
  Column,
  Heading,
  Tag,
  InlineLoading,
  InlineNotification,
  ProgressIndicator,
  ProgressStep,
} from '@carbon/react';
import { User, Home, Enterprise, Building } from '@carbon/icons-react';

interface AccountType {
  type: string;
  name: string;
  description: string;
  icon: string;
  features: string[];
  pricing: string;
}

interface AccountTypeSelectorProps {
  onAccountTypeSelected: (accountType: string, requirements: any) => void;
  loading?: boolean;
  error?: string;
}

const AccountTypeSelector: React.FC<AccountTypeSelectorProps> = ({ onAccountTypeSelected, loading = false, error }) => {
  const [selectedType, setSelectedType] = useState<string>('');
  const [validating, setValidating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Default account types with Carbon icons
  const defaultAccountTypes: AccountType[] = [
    {
      type: 'individual',
      name: 'Individual Professional',
      description: 'Self-paced learning for professionals',
      icon: 'User',
      features: ['Certifications', 'Portfolio', 'Progress Tracking'],
      pricing: 'Free tier available',
    },
    {
      type: 'family',
      name: 'Family Learning',
      description: 'Parent-managed learning for children',
      icon: 'Home',
      features: ['Parent Dashboard', 'Child Progress', 'COPPA Compliant'],
      pricing: 'Starting at $9.99/month',
    },
    {
      type: 'corporate',
      name: 'Corporate Team',
      description: 'Team learning and development',
      icon: 'Enterprise',
      features: ['Team Analytics', 'Admin Controls', 'Bulk Management'],
      pricing: 'Contact sales',
    },
    {
      type: 'school',
      name: 'Educational Institution',
      description: 'School-managed student learning',
      icon: 'Building',
      features: ['Student Management', 'Curriculum Alignment', 'FERPA Compliant'],
      pricing: 'Contact sales',
    },
  ];

  const [availableTypes, setAvailableTypes] = useState<AccountType[]>(defaultAccountTypes);

  useEffect(() => {
    // Load available account types from API
    loadAvailableTypes();
  }, []);

  const loadAvailableTypes = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'https://staging.sigma08.com';
      const response = await fetch(`${apiUrl}/api/v1/accounts/types/available`);
      if (response.ok) {
        const data = await response.json();
        setAvailableTypes(data.available_types || defaultAccountTypes);
      } else {
        setAvailableTypes(defaultAccountTypes);
      }
    } catch (error) {
      console.error('Failed to load account types:', error);
      setAvailableTypes(defaultAccountTypes);
    }
  };

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'User':
        return <User size={32} />;
      case 'Home':
        return <Home size={32} />;
      case 'Enterprise':
        return <Enterprise size={32} />;
      case 'Building':
        return <Building size={32} />;
      default:
        return <User size={32} />;
    }
  };

  const validateAccountType = async (accountType: string) => {
    setValidating(true);
    setCurrentStep(1);

    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'https://staging.sigma08.com';
      const response = await fetch(`${apiUrl}/api/v1/accounts/validate-type`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accountType: accountType,
          tenant_context: null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentStep(2);
        setTimeout(() => {
          onAccountTypeSelected(accountType, data);
        }, 500);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Validation failed');
      }
    } catch (error) {
      console.error('Account type validation failed:', error);
      setValidating(false);
      setCurrentStep(0);
    }
  };

  const handleAccountTypeSelect = (accountType: string) => {
    setSelectedType(accountType);
    validateAccountType(accountType);
  };

  const progressSteps = [
    { label: 'Select Account Type', description: 'Choose your learning path' },
    { label: 'Validate Selection', description: 'Verify requirements' },
    { label: 'Continue Setup', description: 'Proceed to registration' },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <InlineLoading description="Loading account types..." />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: 'var(--cds-spacing-06)' }}>
      <Stack gap={'var(--cds-spacing-07)'}>
        {/* Header */}
        <div>
          <Heading style={{ color: 'var(--cds-text-primary)' }}>Choose Your Account Type</Heading>
          <p
            style={{
              marginTop: 'var(--cds-spacing-03)',
              color: 'var(--cds-text-secondary)',
              fontSize: 'var(--cds-body-short-01-font-size)',
              lineHeight: 'var(--cds-body-short-01-line-height)',
            }}
          >
            Select the account type that best fits your learning needs. You can change this later in your settings.
          </p>
        </div>

        {/* Progress Indicator */}
        <ProgressIndicator currentIndex={currentStep} spaceEqually>
          {progressSteps.map((step, index) => (
            <ProgressStep
              key={index}
              label={step.label}
              description={step.description}
              complete={currentStep > index}
              current={currentStep === index}
            />
          ))}
        </ProgressIndicator>

        {/* Error Display */}
        {error && <InlineNotification kind="error" title="Error" subtitle={error} hideCloseButton />}

        {/* Account Type Selection Grid */}
        <Grid>
          {availableTypes.map(accountType => (
            <Column lg={6} md={8} sm={4} key={accountType.type}>
              <ClickableTile
                onClick={() => handleAccountTypeSelect(accountType.type)}
                style={{
                  height: '100%',
                  minHeight: '280px',
                  padding: 'var(--cds-spacing-05)',
                  cursor: validating ? 'not-allowed' : 'pointer',
                  opacity: validating && selectedType !== accountType.type ? 0.5 : 1,
                  border:
                    selectedType === accountType.type
                      ? '2px solid var(--cds-interactive-01)'
                      : '1px solid var(--cds-border-subtle-01)',
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: 'var(--cds-layer-01)',
                  borderRadius: 'var(--cds-border-radius)',
                  ':hover': {
                    backgroundColor: 'var(--cds-layer-hover-01)',
                  },
                  ':focus': {
                    outline: '2px solid var(--cds-focus)',
                    outlineOffset: '2px',
                  },
                }}
                disabled={validating}
              >
                <Stack gap={'var(--cds-spacing-05)'}>
                  {/* Icon and Title */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--cds-spacing-04)' }}>
                    <div
                      style={{
                        color: 'var(--cds-icon-primary)',
                        backgroundColor: 'var(--cds-layer-accent-01)',
                        borderRadius: 'var(--cds-border-radius)',
                        padding: 'var(--cds-spacing-04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {getIconComponent(accountType.icon)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <Heading
                        size="compact-02"
                        style={{
                          marginBottom: 'var(--cds-spacing-02)',
                          color: 'var(--cds-text-primary)',
                        }}
                      >
                        {accountType.name}
                      </Heading>
                      <p
                        style={{
                          color: 'var(--cds-text-secondary)',
                          fontSize: 'var(--cds-body-short-01-font-size)',
                          lineHeight: 'var(--cds-body-short-01-line-height)',
                        }}
                      >
                        {accountType.description}
                      </p>
                    </div>
                  </div>

                  {/* Features */}
                  <div>
                    <p
                      style={{
                        fontWeight: 600,
                        marginBottom: 'var(--cds-spacing-04)',
                        fontSize: 'var(--cds-label-01-font-size)',
                        color: 'var(--cds-text-primary)',
                      }}
                    >
                      Key Features:
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--cds-spacing-03)' }}>
                      {accountType.features.map((feature, index) => (
                        <Tag key={index} type="cool-gray" size="md">
                          {feature}
                        </Tag>
                      ))}
                    </div>
                  </div>

                  {/* Pricing */}
                  <div
                    style={{
                      marginTop: 'auto',
                      paddingTop: 'var(--cds-spacing-04)',
                      borderTop: '1px solid var(--cds-border-subtle-01)',
                    }}
                  >
                    <p
                      style={{
                        fontWeight: 600,
                        color: 'var(--cds-text-interactive)',
                        fontSize: 'var(--cds-body-long-02-font-size)',
                      }}
                    >
                      {accountType.pricing}
                    </p>
                  </div>

                  {/* Loading indicator for selected type */}
                  {validating && selectedType === accountType.type && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'var(--cds-spacing-04)',
                        right: 'var(--cds-spacing-04)',
                        background: 'var(--cds-background)',
                        borderRadius: '50%',
                        padding: 'var(--cds-spacing-03)',
                      }}
                    >
                      <InlineLoading description="" />
                    </div>
                  )}
                </Stack>
              </ClickableTile>
            </Column>
          ))}
        </Grid>

        {/* Help Text */}
        <Tile style={{ backgroundColor: 'var(--cds-layer-02)' }}>
          <Stack gap={'var(--cds-spacing-03)'}>
            <Heading size="compact-01" style={{ color: 'var(--cds-text-primary)' }}>
              Need help choosing?
            </Heading>
            <p
              style={{
                fontSize: 'var(--cds-caption-01-font-size)',
                lineHeight: 'var(--cds-caption-01-line-height)',
                color: 'var(--cds-text-secondary)',
              }}
            >
              • <strong>Individual Professional:</strong> Perfect for self-directed learners looking to advance their
              careers
              <br />• <strong>Family Learning:</strong> Ideal for parents managing their children's educational journey
              <br />• <strong>Corporate Team:</strong> Designed for organizations investing in employee development
              <br />• <strong>Educational Institution:</strong> Built for schools and universities managing student
              learning
            </p>
          </Stack>
        </Tile>
      </Stack>
    </div>
  );
};

export default AccountTypeSelector;
